import {useState, useMemo} from "react"
import {UseQueryResult} from "./useQuery"
import {PBApolloError} from "./helpers/hookShared"

export interface FetchMoreState {
  loading: boolean
  error: string | undefined
  hasMore: boolean
}

/**
 * Wrapper around a query's `fetchMore` function to handle offset pagination and error/loading states
 */
export function useFetchMore<T extends {hasMore?: boolean; offset?: number}>(
  apollo: UseQueryResult<T, any>
): [() => Promise<undefined>, FetchMoreState] {
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  const {data, variables, fetchMore} = apollo
  const hasMore = data?.hasMore ?? false
  const offset = data?.offset ?? 0

  return useMemo(() => {
    return [
      async function wrappedFetchMore() {
        if (hasMore) {
          try {
            setLoading(true)
            await fetchMore({variables: {...variables, offset}})
          } catch (e) {
            setError((e as PBApolloError).networkError.formattedMessage)
          } finally {
            setLoading(false)
          }
        }
        return undefined
      },
      {
        loading,
        error,
        hasMore
      }
    ] as const
  }, [error, fetchMore, hasMore, loading, offset, variables])
}
